import {
  CellClassParams,
  CellDoubleClickedEvent,
  CellFocusedEvent,
  CellKeyDownEvent,
  CellStyle,
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GridOptions,
  ITooltipParams,
  RowNode,
  SortChangedEvent,
  ValueFormatterParams,
} from '@ag-grid-community/core'
import { DOCUMENT } from '@angular/common'
import {
  Component,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core'
import { FormControl, ValidationErrors } from '@angular/forms'
import {
  EditableCheckboxRenderer,
  heliosDefaultColDef,
  heliosDefaultColWidth,
  heliosSizingAdjustments,
} from '@helios/ag-grid-config'
import { ExternalTransaction } from '@helios/common-api'
import { BlankSessionService } from '@helios/sessions'
import {
  AccountInfo,
  RootState,
  selectActiveSessionReconDate,
  selectActualReconDate,
  selectFirstFocusedAccountInfo,
  selectFirstLoadedAccountId,
  selectLegacyUserId,
  selectSelectedReconDate,
} from '@helios/state-management'
import { TelemetryService } from '@helios/telemetry'
import {
  UserPreferencesService,
  WorkspaceGridSavedViewService,
} from '@helios/user-preferences'
import {
  NgbActiveModal,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { AgGridApi, NfTooltipParams } from '@northfork/ag-grid'
import {
  InlineEditDataType,
  getInlineEditColumnType,
} from '@northfork/ag-grid-angular'
import { ISODateFormat, parseDate } from '@northfork/basics'
import { InlineEditDisplayOptions, ToastService } from '@northfork/ng-basics'
import { isAfter } from 'date-fns'
import { Observable, Subject } from 'rxjs'
import { debounceTime, take, takeUntil } from 'rxjs/operators'
import {
  BulkFxForwardRow,
  fxForwardField,
} from '../../../model/bulk-transaction/bulk-fx/fx-spot.model'
import { SecurityIdRequest } from '../../../model/bulk-transaction/bulk-misc-model'
import {
  BulkTransactionModalInput,
  BulkTransactionModalMode,
} from '../../../model/bulk-transaction/bulk-popup-input-model'
import {
  BulkSleeveSimpleAccounts,
  SleeveSimple,
} from '../../../model/bulk-transaction/bulk-sleeve-simple-accounts.model'
import {
  BulkFXForwardTransaction,
  ClearwaterTransactionDetailResponse,
  MetaDataTransaction,
  TransactionRequestWrapper,
} from '../../../model/bulk-transaction/bulk-transaction'
import { BulkTransactionRequest } from '../../../model/bulk-transaction/bulk-transaction.model'
import { BulkFXForwardValidationView } from '../../../model/bulk-transaction/bulk-validation-view.model'
import { CounterParty } from '../../../model/counter-party.model'
import { SecurityMasterData } from '../../../model/security.model'
import { ExternalUnmatchedTransaction } from '../../../model/single-transaction/external-unmatched-transaction'
import { Transaction } from '../../../model/single-transaction/transaction'
import {
  TranCategory,
  TranCategoryUrl,
} from '../../../model/transaction-category.model'
import {
  ErrorSeverity,
  ValidationInfo,
} from '../../../model/validation-response.model'
import { BulkDeleteService } from '../../../services/bulk-transaction/bulk-delete.service'
import { PasteService } from '../../../services/bulk-transaction/paste.service'
import { CopyService } from '../../../services/copy.service'
import { CreateEditTransactionService } from '../../../services/create-edit-transaction.service'
import { ModalFocusService } from '../../../services/modal-focus.service'
import {
  isBulkCopyModal,
  isBulkEditModal,
  isBulkExternalModal,
} from '../../../util/bulk-popup-input-utils'
import {
  BULK_DIALOG_EVENT_NAME,
  bulkMetricsDetail,
  bulkRequestMode,
  customSortComparatorBulk,
  customSortComparatorBulkNumeric,
  customSortComparatorBulkValidatorColumn,
  fillHandleDoubleClickHandler,
  getAccountCellParams,
  numberValueFormatter,
  onSortChange,
  showOrderIDColumn,
  showTranIdColumn,
} from '../../../util/bulk-specific-common-utils'
import {
  mapBulkExternalTransaction,
  mapFxForwardToBulkTransaction,
} from '../../../util/bulk-transaction-wrapper-util'
import {
  activateDialog,
  deduceValidityStatusCellClass,
  emptySelectedCellsOnValidKeyPress,
  getDefaultUnknownCounterParty,
  getFieldValidatorFormatter,
  isDialogComponentInActive,
  markRowsMissingAccount,
} from '../../../util/common-utils'
import { TransactionConstants } from '../../../util/constants'
import { BulkTransactionModalResponse } from '../bulk-transaction-modal-response'
import { BulkTransactionsSaveConfirmationModalComponent } from '../bulk-transactions-confirm-button-modal/bulk-transactions-confirm-button-modal.component'
import { ErrorTooltipComponent } from '../error-Tooltip/error-tooltip.component'
@Component({
  selector: 'hs-bulk-fx-forward',
  templateUrl: './bulk-fx-forward.component.html',
  styleUrls: ['./bulk-fx-forward.component.scss'],
  providers: [PasteService, CopyService, BulkDeleteService],
})
export class BulkFxForwardComponent implements OnInit, OnDestroy {
  @Input() transInput?: BulkTransactionModalInput

  private readonly onPasteEndBinding = this.onPasteEnd.bind(this)

  title = 'Fx Forward Bulk Create'
  loadingDialog = false
  lastPastedRowIds: string[] = []
  lastColumnIdsPasted: Set<string> = new Set<string>()
  getFieldValidatorFormatter = getFieldValidatorFormatter
  formSubmitting = false
  allDataValidated = false
  transactionLoaded = false
  sortable = false
  rowData: BulkFxForwardRow[] = []
  colDefs: ColDef[] = this.getColDefs()
  gridOptions: GridOptions = this.createGridOptions()
  cellLevelErrorMessageMap = new Map<string, string[]>()
  cellLevelWarningMessageMap = new Map<string, string[]>()
  userId?: number
  forbiddenErrors = new FormControl('')
  isEdit = false
  note = new FormControl('')
  deletePostProcessingInProgress = false
  reconDate?: Date
  sessionReconDate?: Date
  selectedReconDate?: Date
  windowDate?: Date
  isCommonValidationError = false
  disableSaveValidRows = false
  isDataValid = false
  isValidating = false
  disableValidate = false
  counterPartyList: CounterParty[] = []
  private readonly destroySubject$ = new Subject<void>()
  errorFields: string[] = []
  gridApi?: AgGridApi
  noteWarning: ValidationErrors | null = null
  currentNoteError: ValidationErrors | null =
    TransactionConstants.DEFAULT_NOTE_VALIDATION_ERROR
  genericErrors: string[] = []
  genericWarnings: string[] = []
  hasTransactionMatchCustody = false
  hasTransactionMatchTrading = false
  currentCounterParty?: CounterParty
  currentRowIndex = 0
  loadingCounterParty = true
  disableClear = false
  originalTransactions: BulkFXForwardTransaction[] = []
  externalTransaction: ExternalTransaction[] = []
  bulkExternalTransactionDetail: ExternalUnmatchedTransaction[] = []
  securityMasterData: Map<string, SecurityMasterData> = new Map<
    string,
    SecurityMasterData
  >()
  clearwaterTransactionDetail: { [s: string]: Transaction } = {}
  sessionAccountInfo?: AccountInfo
  bulkSleeveSimpleAccount: BulkSleeveSimpleAccounts[] = []
  sleeveSimpleAccount: SleeveSimple[] = []
  sleeveAccountIds: number[] = []
  loadingText = TransactionConstants.LOAD_TRANSACTION
  errorText = ''
  errorHeader: string | undefined
  errorDetails: unknown
  activeInstances: NgbModalRef[] = []
  compId = 'bulk-fx-forward-' + Math.random()
  showConfirmButtonModal = false

  constructor(
    public toastService: ToastService,
    readonly activeModal: NgbActiveModal,
    readonly modalService: NgbModal,
    readonly store: Store<RootState>,
    readonly bulkTransactionService: CreateEditTransactionService,
    private readonly savedViewService: WorkspaceGridSavedViewService,
    private readonly blankSessionService: BlankSessionService,
    private readonly pasteService: PasteService,
    private readonly copyService: CopyService,
    private readonly bulkDeleteService: BulkDeleteService,
    private readonly userPreferencesService: UserPreferencesService,
    private readonly telemetryService: TelemetryService,
    private readonly elRef: ElementRef,
    private readonly modalFocusService: ModalFocusService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {
    this.userPreferencesService
      .getBulkDialogSortPromptPreference()
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe(sortable => {
        this.sortable = sortable
        if (this.gridOptions.defaultColDef) {
          this.gridOptions.defaultColDef.sortable = sortable
          this.gridOptions.defaultColDef.comparator = customSortComparatorBulk
        }
      })
    this.modalService.activeInstances
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(activeInstances => {
        this.activeInstances = activeInstances
      })
  }

  async ngOnInit() {
    const startTime = Date.now()

    activateDialog(this.activeInstances, this.compId, this.elRef)

    this.modalFocusService
      .outsideFocus(this.elRef)
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(() => this.disableGridFocus())

    this.note.valueChanges
      .pipe(debounceTime(500), takeUntil(this.destroySubject$))
      .subscribe(() => {
        this.setNoteErrors()
      })
    if (this.transInput) {
      showTranIdColumn(this.gridOptions, this.transInput, this.sortable)
      if (
        isBulkEditModal(this.transInput) &&
        this.transInput.transactions?.[0].orderId
      ) {
        showOrderIDColumn(this.gridOptions, this.sortable)
      }
    }
    try {
      await this.init()
    } catch (error) {
      this.errorText = TransactionConstants.ERROR_LOADING_TRANSACTION
      this.errorDetails = error.error
    }
    this.note.markAsTouched()
    this.note.setErrors(this.currentNoteError)
    const now = Date.now()
    const [count, type] = bulkMetricsDetail(
      this.rowData.length,
      this.transInput,
    )
    this.telemetryService.emitCustomEvent(BULK_DIALOG_EVENT_NAME, {
      dialogName: 'Currency-Forward-' + type,
      durationMs: now - startTime,
      tranCount: count,
    })
  }

  private async init() {
    this.loadingDialog = true
    this.document.addEventListener('pasteDone', this.onPasteEndBinding)
    this.bulkTransactionService
      .loadCounterParties()
      .pipe(takeUntil(this.destroySubject$))
      .subscribe(counterParties => {
        this.counterPartyList = counterParties
        this.loadingCounterParty = false
      })
    if (this.transInput?.extTranId) {
      await this.loadExtTransactionDetailBulk(this.transInput.extTranId)
    }
    this.loadDataCreate()
    if (this.transInput?.bulkStatus) {
      this.transInput.externalTransaction = mapBulkExternalTransaction(
        this.bulkExternalTransactionDetail,
        this.transInput.bulkStatus,
      )
    }
    if (this.transInput?.cwTranId)
      await this.loadTransactionDetailBulk(
        this.transInput?.cwTranId,
        this.transInput.securityId,
      )
    if (this.transInput?.cwTranId) {
      this.transInput.transactions = mapFxForwardToBulkTransaction(
        this.clearwaterTransactionDetail,
        this.securityMasterData,
      )
    }
    if (
      !isBulkCopyModal(this.transInput) &&
      (this.transInput?.transactions || this.transInput?.externalTransaction)
    ) {
      this.title = 'Fx Forward Bulk Edit'
      this.disableClear = true
      this.isEdit = true
      this.pasteService.setIsEdit()
      this.loadDataEdit()
      if (isBulkEditModal(this.transInput)) {
        this.originalTransactions = this.transInput
          ?.transactions as BulkFXForwardTransaction[]

        const fxTransactions = this.transInput
          ?.transactions as BulkFXForwardTransaction[]

        fxTransactions.forEach((fx, index) => {
          if (
            fx.counterParty &&
            !this.counterPartyList.find(cp => cp.name == fx.counterParty?.name)
          ) {
            this.counterPartyList.push(fx.counterParty)
          }
          this.rowData.push(this.generateDataEntryFromFx(fx, index))
        })
      } else if (isBulkExternalModal(this.transInput)) {
        this.externalTransaction = this.transInput
          ?.externalTransaction as ExternalTransaction[]

        if (!this.transInput?.extTranId) {
          const uniqueAccountIdExternal = [
            ...new Set(this.externalTransaction.map(obj => obj.accountId)),
          ]
          await this.loadNonSessionAccountTypeInfo(uniqueAccountIdExternal)
        }
        this.externalTransaction.forEach((fx, index) => {
          if (
            fx.clearwaterCounterParty &&
            !this.counterPartyList.find(
              cp => cp.name == fx.clearwaterCounterParty?.name,
            )
          ) {
            this.counterPartyList.push(fx.clearwaterCounterParty)
          }
          this.rowData.push(this.generateDataEntryFromExternalFx(fx, index))
        })
      }
    } else if (isBulkCopyModal(this.transInput)) {
      this.originalTransactions = this.transInput
        ?.transactions as BulkFXForwardTransaction[]

      const fxTransactions = this.transInput
        ?.transactions as BulkFXForwardTransaction[]

      fxTransactions.forEach((fx, index) => {
        if (
          fx.counterParty &&
          !this.counterPartyList.find(cp => cp.name == fx.counterParty?.name)
        ) {
          this.counterPartyList.push(fx.counterParty)
        }
        this.rowData.push(this.generateDataEntryFromFx(fx, index))
      })
      this.rowData.push(
        this.generateEmptyDataEntry(false, this.rowData.length + 1),
      )
    } else {
      this.createFreshRowData()
    }
    this.gridApi?.api.applyTransaction({ add: this.rowData })

    this.setSleeveSimpleAccountIdsPerRow()

    this.loadingDialog = false
  }

  ngOnDestroy(): void {
    this.pasteService.destroy()
    this.copyService.destroy()
    this.destroySubject$.next()
    this.destroySubject$.complete()

    this.document.removeEventListener('pasteDone', this.onPasteEndBinding)
  }

  setSleeveSimpleAccountIdsPerRow() {
    if (this.bulkSleeveSimpleAccount.length > 0)
      this.rowData.forEach(node => {
        node.sleeveSimpleAccountIds = this.bulkSleeveSimpleAccount
          .filter(
            sleeveSimpleId =>
              sleeveSimpleId.id === Number(node.externalAccountId),
          )
          .map(data => data.sleeveSimple)[0]
      })
    else
      this.rowData.forEach(
        node => (node.sleeveSimpleAccountIds = this.sleeveSimpleAccount),
      )
  }

  /** @internal */
  loadDataCreate() {
    this.store
      .select(selectActualReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((reconDate?: Date) => {
        this.reconDate = reconDate
      })
    this.store
      .select(selectActiveSessionReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((sessionReconDate?: Date) => {
        this.sessionReconDate = sessionReconDate
      })
    this.store
      .select(selectSelectedReconDate)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((selectedReconDate?: Date) => {
        this.selectedReconDate = selectedReconDate
      })
    this.store
      .select(selectLegacyUserId)
      .pipe(take(1), takeUntil(this.destroySubject$))
      .subscribe((userId?: number) => {
        this.userId = userId
      })
  }

  /** @internal */
  async loadDataEdit() {
    const reconDate$ = this.store.select(selectActualReconDate).pipe(take(1))
    const userId$ = this.store.select(selectLegacyUserId).pipe(take(1))
    return Promise.all([reconDate$.toPromise(), userId$.toPromise()]).then(
      ([reconDate, userId]) => {
        this.reconDate = reconDate
        this.userId = userId
      },
    )
  }

  setNoteErrors() {
    if (this.currentNoteError != null) {
      this.note.setErrors(this.currentNoteError)
    }
  }
  private createGridOptions(): GridOptions {
    return {
      ...heliosSizingAdjustments,
      onGridReady: async event => {
        this.gridApi = event
        this.pasteService.registerGrid(event.api, event.columnApi)
        this.copyService.registerGrid(event.api)
        this.bulkDeleteService.registerGrid(event.api)
        await this.savedViewService.manageSavedView(
          event,
          'Bulk-currency-forward-workspace-key',
          this.destroySubject$,
        )
        this.gridApi.api.setSortModel(null)
      },
      getRowNodeId: ({ rowId }: BulkFxForwardRow) => rowId,
      defaultColDef: {
        ...heliosDefaultColDef,
        menuTabs: [],
        minWidth: heliosDefaultColWidth.ValidityStatusCol,
        sortable: false,
      },
      sideBar: false,
      rowSelection: 'multiple',
      onCellDoubleClicked: (event: CellDoubleClickedEvent) => {
        const element = event.event?.target as HTMLElement
        if (element.className == 'ag-fill-handle') {
          fillHandleDoubleClickHandler(event.api)
        } else {
          event.api.startEditingCell({
            rowIndex: this.currentRowIndex,
            colKey: event.column.getColId(),
          })
        }
      },
      suppressMultiRangeSelection: false,
      enableCellChangeFlash: true,
      enableRangeSelection: true,
      enableFillHandle: true,
      getContextMenuItems: () => this.getContextMenuItems(),
      columnDefs: this.colDefs,
      onFirstDataRendered: params => {
        params.api.sizeColumnsToFit()
      },
      onSortChanged: (params: SortChangedEvent) => onSortChange(params),
      onCellValueChanged: (event: CellValueChangedEvent) => {
        this.onColumnValueChanged(event)
      },
      onCellFocused: (event: CellFocusedEvent) => {
        this.handleCellFocus(event)
      },
      onCellKeyDown: (event: CellKeyDownEvent) => {
        if (event.api.getEditingCells().length == 0) {
          emptySelectedCellsOnValidKeyPress(
            (event.event as KeyboardEvent).key,
            event.api,
          )
        }
      },
    }
  }

  private getColDefs(): ColDef[] {
    return [
      {
        field: fxForwardField('validityStatus'),
        headerName: '',
        valueGetter: () => '',
        cellClass: (params: { data: { validityStatus: string } }) =>
          deduceValidityStatusCellClass(params.data.validityStatus),
        tooltipField: 'errorMessageMap',
        tooltipComponentParams: {
          showTooltip: params =>
            params?.data.errorMessageMap &&
            params.data.errorMessageMap.size > 0,
        } as NfTooltipParams,
        width: heliosDefaultColWidth.ValidityStatusCol,
        tooltipComponent: ErrorTooltipComponent,
        comparator: customSortComparatorBulkValidatorColumn,
      },
      {
        field: fxForwardField('rowNumber'),
        headerName: '#',
        valueGetter: 'node.rowIndex + 1',
        width: heliosDefaultColWidth.SerialNumberCol,
        sortable: false,
      },
      {
        field: fxForwardField('errorMessageMap'),
        hide: true,
      },
      {
        field: fxForwardField('account'),
        headerName: 'Account ID',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        width: 85,
        cellEditorSelector: (x: EditableCallbackParams) =>
          getAccountCellParams(
            x,
            !!this.transInput?.externalTransaction,
            this.sessionAccountInfo?.accountType,
          ),
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        editable: (x: EditableCallbackParams) => this.isAccountEditable(x),
      },
      {
        field: fxForwardField('entryDate'),
        headerName: 'Entry Date',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Date,
        },

        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: fxForwardField('settleDate'),
        headerName: 'Settle Date',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Date,
        },

        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },

      {
        field: fxForwardField('fromSecurityCusip'),
        headerName: 'From Asset ID',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.String,

          disablePopover: true,
        },
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        editable: () => !isBulkEditModal(this.transInput),
      },
      {
        field: fxForwardField('toSecurityCusip'),
        headerName: 'To Asset ID',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.String,

          disablePopover: true,
        },
        cellStyle: (params: CellClassParams) => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
        editable: () => !isBulkEditModal(this.transInput),
      },
      {
        field: fxForwardField('fromUnits'),
        headerName: 'From Units',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        comparator: customSortComparatorBulkNumeric,
        valueFormatter: (params: ValueFormatterParams) =>
          params.value !== ''
            ? numberValueFormatter(Number(params.value), 2, 4)
            : '',
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: fxForwardField('toUnits'),
        headerName: 'To Units',
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.Number,
          disablePopover: true,
        },
        comparator: customSortComparatorBulkNumeric,
        valueFormatter: (params: ValueFormatterParams) =>
          params.value !== ''
            ? numberValueFormatter(Number(params.value), 2, 4)
            : '',
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },

      {
        field: fxForwardField('counterParty'),
        headerName: 'Counter Party',
        width: 195,
        type: getInlineEditColumnType(),
        cellRendererParams: {
          editButtonClickOnly: true,
        } as InlineEditDisplayOptions,
        cellEditorParams: {
          dataType: InlineEditDataType.String,

          disablePopover: true,
        },
        cellStyle: params => {
          return this.getCellStyle(params)
        },
        tooltipValueGetter: params => {
          return this.toolTipValueGetter(params)
        },
        tooltipComponentParams: {
          showTooltip: params => this.showToolTip(params),
        } as NfTooltipParams,
      },
      {
        field: fxForwardField('cleared'),
        headerName: 'Cleared',
        cellRenderer: EditableCheckboxRenderer,
        width: heliosDefaultColWidth.Id,
      },
      {
        field: fxForwardField('transactionTypeLabel'),
        hide: true,
      },
    ]
  }

  deduceTranTypeCellStyle(params: CellClassParams) {
    if (
      params.data.tranType == '' &&
      params.data.validityStatus != 'inactive'
    ) {
      return { 'background-color': 'lightcoral' }
    } else {
      return this.getCellStyle(params)
    }
  }

  deduceTranTypeToolTip(params: ITooltipParams) {
    if (
      params.data.tranType == '' &&
      params.data.validityStatus != 'inactive'
    ) {
      return 'Valid Value Required'
    } else {
      return this.toolTipValueGetter(params)
    }
  }
  handleCellFocus(event: CellFocusedEvent) {
    this.currentRowIndex = event.rowIndex ?? 0
  }
  /**
   * The right click magic
   *
   * @internal
   */
  getContextMenuItems() {
    return [
      {
        name: 'Remove Row(s)',
        tooltip: 'Remove selected row(s)',
        action: () => {
          this.bulkDeleteService.deleteHandler()
        },
      },
      'export',
    ]
  }

  showToolTip(params?: ITooltipParams) {
    const impactedFieldColDef = params?.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params?.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)
    if (
      (currentValidationErrors && currentValidationErrors?.length > 0) ||
      (currentValidationWarnings && currentValidationWarnings?.length > 0)
    ) {
      return true
    }
    return false
  }

  toolTipValueGetter(params: ITooltipParams): string {
    const impactedFieldColDef = params.colDef as ColDef
    const impactedFieldProcessed = this.deduceImpactedField(
      impactedFieldColDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    const currentValidationErrors = this.cellLevelErrorMessageMap.get(cellKey)
    const currentValidationWarnings =
      this.cellLevelWarningMessageMap.get(cellKey)

    if (currentValidationErrors) {
      return currentValidationErrors.join(' | ')
    } else if (currentValidationWarnings) {
      return currentValidationWarnings.join(' | ')
    } else {
      return impactedFieldColDef?.field == 'counterParty' ? params.value : ''
    }
  }
  deduceImpactedField(impactedFieldFromValidationInfo: string): string {
    if (impactedFieldFromValidationInfo == 'accountId') {
      return 'account'
    }
    if (impactedFieldFromValidationInfo == 'toSecurityId') {
      return 'toSecurityCusip'
    }
    if (impactedFieldFromValidationInfo == 'fromSecurityId') {
      return 'fromSecurityCusip'
    } else {
      return impactedFieldFromValidationInfo
    }
  }
  getCellStyle(params: CellClassParams): CellStyle {
    const impactedFieldProcessed = this.deduceImpactedField(
      params.colDef.field ?? '',
    )
    const index = params.data.rowId
    const cellKey = `${index}-${impactedFieldProcessed}`
    if (this.cellLevelErrorMessageMap.has(cellKey)) {
      return { 'background-color': 'lightcoral' }
    } else if (this.cellLevelWarningMessageMap.has(cellKey)) {
      return { 'background-color': 'yellow' }
    } else {
      return { 'background-color': '#00000000' }
    }
  }

  async onPasteEnd(evt: Event) {
    const pasteDetails = (evt as CustomEvent).detail
    this.lastPastedRowIds = pasteDetails.pastedRowData.map(
      (rowData: { rowId: BulkFXForwardTransaction }) => rowData.rowId,
    )
    this.lastColumnIdsPasted = pasteDetails.colPastedSet
    this.applyOnChangeForPastedRowNodes()
  }

  applyOnChangeForPastedRowNodes() {
    this.lastPastedRowIds.forEach(rowId => {
      const displayedNode = this.gridApi?.api.getRowNode(rowId)
      if (displayedNode) {
        this.applyOnChangeForPastedRowNode(displayedNode)
      }
    })
  }

  applyOnChangeForPastedRowNode(displayedNode: RowNode) {
    const allColumns = this.gridApi?.columnApi.getAllColumns()
    allColumns
      ?.filter(column =>
        this.lastColumnIdsPasted.has(column.getColDef().field ?? ''),
      )
      .forEach(currentColumn => {
        const colId = currentColumn.getColDef().field
        this.onColumnValueChanged({
          column: { getColId: () => colId },
          node: displayedNode,
          data: displayedNode.data,
          newValue: displayedNode?.data[colId ?? ''],
          oldValue: '',
        } as unknown as CellValueChangedEvent)
      })
  }

  onColumnValueChanged(event: CellValueChangedEvent) {
    const rowNode = event.node
    const colId = event.column.getColId()
    if (
      rowNode.data[colId] === event.oldValue ||
      this.deletePostProcessingInProgress
    ) {
      return
    }

    if (
      rowNode?.data.validityStatus == 'inactive' &&
      (this.transInput?.modalMode == BulkTransactionModalMode.CreateCWTran ||
        this.transInput?.modalMode == BulkTransactionModalMode.CopyCWTran) &&
      colId != 'validityStatus' &&
      colId != 'errorMessageMap'
    ) {
      if (
        Number(rowNode?.rowIndex) + 1 ==
        this.gridApi?.api.getDisplayedRowCount()
      ) {
        const rows = [
          this.generateEmptyDataEntry(false, Number(rowNode.data.rowId) + 1),
        ]
        this.gridApi?.api.applyTransaction({ add: rows })
      }
    }

    if (
      colId != 'validityStatus' &&
      colId != 'errorMessageMap' &&
      rowNode.data.validityStatus != 'intermediate'
    ) {
      rowNode.data.validityStatus = 'intermediate'
      this.allDataValidated = false
    }

    if (colId == 'account') {
      if (this.bulkSleeveSimpleAccount.length > 0) {
        this.sleeveSimpleAccount = this.bulkSleeveSimpleAccount
          .filter(data => data.id === Number(rowNode.data.externalAccountId))
          .map(data => data.sleeveSimple)[0]
      }
      if (this.sleeveSimpleAccount && this.sleeveSimpleAccount.length > 0) {
        const sAccount = this.sleeveSimpleAccount?.filter(
          data => data.displayLabel == rowNode.data.account,
        )[0]?.id
        if (sAccount) {
          rowNode.data.sleeveAccountId = sAccount
        } else {
          rowNode.setDataValue('account', '')
        }
      }
    } else if (colId == 'settleDate') {
      this.clearUnclearTransaction(event)
    }
  }

  disableSaveValidRowsButton() {
    let disable = true

    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus == 'valid' ||
        node.data.validityStatus == 'warning'
      ) {
        disable = false // If at least one node has validity 'valid' or 'warning', set disable to false
        return // Exit the loop early if validity is 'valid'
      }
    })

    // Set disableSaveValidRows based on the value of disable
    this.disableSaveValidRows = disable
  }

  // Flags active rows with no account as invalid client-side so the user gets an
  // inline "Account is required" error on the cell instead of a silently disabled
  // Validate button. Returns true if any row was missing an account, in which case
  // validate() short-circuits and never sends accountId 0 to the backend.
  markMissingAccountRows(): boolean {
    const anyMissing = markRowsMissingAccount(
      this.gridApi?.api,
      'account',
      this.deduceImpactedField('account'),
      this.cellLevelErrorMessageMap,
    )
    if (anyMissing) {
      this.isDataValid = false
    }
    return anyMissing
  }

  async validate() {
    this.gridApi?.api?.clearRangeSelection()
    this.isDataValid = true
    this.isCommonValidationError = false
    if (this.markMissingAccountRows()) {
      return
    }
    const bulkValidateView = this.createBulkValidationView()
    this.isValidating = true
    this.errorDetails = ''
    try {
      this.loadingText = TransactionConstants.VALIDATE_TRANSACTION
      const validationInfos = await this.bulkTransactionService
        .validateBulkTransaction(
          bulkValidateView,
          TranCategoryUrl.CURRENCY_FORWARD,
        )
        .toPromise()
      this.handleBulkValidationResponse(validationInfos.validationMap)
      this.handleCommonValidations(validationInfos.commonValidations)
      this.allDataValidated = true
      this.disableSaveValidRowsButton()
      this.isValidating = false
    } catch (error) {
      this.errorHeader = TransactionConstants.VALIDATION_ERROR
      this.errorDetails = error.error
      this.isValidating = false
    }
  }

  handleCommonValidations(commonValidations: ValidationInfo[]) {
    this.genericErrors = []
    this.genericWarnings = []
    this.currentNoteError = null
    if (commonValidations.length == 0) {
      this.showConfirmButtonModal = false
    }
    commonValidations.forEach(validationInfo => {
      if (validationInfo.impactedFields.length == 0) {
        validationInfo.impactedFields = ['generic']
      }
      validationInfo.impactedFields.forEach(field => {
        if (field == 'transactionNote') {
          this.isDataValid = false
          this.isCommonValidationError = true
          const validatorKey = 'transactionNoteValidator'
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.note.markAsTouched()
            this.currentNoteError = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
            this.note.setErrors(this.currentNoteError)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.noteWarning = {
              [validatorKey]: {
                message: validationInfo.message,
              },
            }
          }
        } else if (
          field == 'confirmButton' &&
          validationInfo.level == ErrorSeverity.WARN
        ) {
          this.showConfirmButtonModal = true
        } else {
          //generic case
          this.isDataValid = false
          this.isCommonValidationError = true
          if (validationInfo.level == ErrorSeverity.ERROR) {
            this.genericErrors.push(validationInfo.message)
          } else if (validationInfo.level == ErrorSeverity.WARN) {
            this.genericWarnings.push(validationInfo.message)
          }
        }
      })
    })
  }

  handleBulkValidationResponse(bulkValidationInfos: {
    [key: number]: ValidationInfo[]
  }) {
    // create errorMessages column per row and update tooltip with it

    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive') {
        rowNode.data.validityStatus = 'valid'
        rowNode.data.errorMessageMap = new Map()
      }
    })

    this.note.setErrors(null)
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    const bulkValidationInfo = new Map<string, ValidationInfo[]>(
      Object.entries(bulkValidationInfos),
    )
    bulkValidationInfo.forEach((rowValidationInfo: ValidationInfo[], index) => {
      // create a map of key index,field and keep all errors(string or string[](if erros ) and warnings (if onlky warnings)
      // in cell class rule make it yellow if warning validity status
      // in tooltip use above map to put all erros in tooltip
      const currentRowNode = this.gridApi?.api.getRowNode(index)
      const errorMessages = new Map<string, string[]>()
      const warningMessages = new Map<string, string[]>()
      rowValidationInfo.forEach((validationInfo: ValidationInfo) => {
        const isError = validationInfo.level === ErrorSeverity.ERROR
        const messageMap = isError
          ? this.cellLevelErrorMessageMap
          : this.cellLevelWarningMessageMap
        const messages = isError ? errorMessages : warningMessages
        validationInfo.impactedFields.forEach((impactedField: string) => {
          const impactedFieldProcessed = this.deduceImpactedField(impactedField)
          const currentKey = `${index}-${impactedFieldProcessed}` // More efficient key representation
          // message map
          const msgList = messageMap.get(currentKey) || []
          msgList.push(validationInfo.message)
          messageMap.set(currentKey, msgList)
          // messages
          const fieldMessages = messages.get(impactedFieldProcessed) || []
          fieldMessages.push(validationInfo.message)
          messages.set(impactedFieldProcessed, fieldMessages)
        })
      })
      if (errorMessages.size > 0 && currentRowNode) {
        this.isDataValid = false
        currentRowNode.data.validityStatus = 'invalid'
        currentRowNode.data.errorMessageMap = errorMessages
      }
      if (
        errorMessages.size == 0 &&
        warningMessages.size > 0 &&
        currentRowNode
      ) {
        currentRowNode.data.validityStatus = 'warning'
        currentRowNode.data.errorMessageMap = warningMessages
      }

      if (rowValidationInfo.length == 0 && currentRowNode) {
        currentRowNode.data.validityStatus = 'valid'
        currentRowNode.data.errorMessageMap = new Map()
      }
    })
    this.gridApi?.api.redrawRows()
  }

  createBulkValidationView(): BulkFXForwardValidationView {
    const view: BulkFXForwardValidationView = {
      transaction: this.getGridTransactions(true),
      requestMode: bulkRequestMode(this.transInput?.modalMode),
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      tranCategory: TranCategory.CURRENCY_FORWARD,
    }
    if (this.note.value != '') view.transactionNote = this.note.value

    return view
  }

  getGridOnlyValidTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (
        rowNode.data.validityStatus == 'valid' ||
        rowNode.data.validityStatus == 'warning'
      )
        transactions.push(
          this.prepareBulkTransactionData(rowNode.data, isValidate),
        )
    })
    return transactions
  }

  getGridTransactions(isValidate: boolean) {
    const transactions: TransactionRequestWrapper[] = []
    this.gridApi?.api?.forEachNode(rowNode => {
      if (rowNode.data.validityStatus != 'inactive')
        transactions.push(
          this.prepareBulkTransactionData(rowNode.data, isValidate),
        )
    })
    return transactions
  }
  prepareBulkTransactionData(
    rowData: BulkFxForwardRow,
    isValidate: boolean,
  ): TransactionRequestWrapper {
    let currentOriginalTransaction = undefined
    if (!isBulkCopyModal(this.transInput)) {
      currentOriginalTransaction = this.originalTransactions.find(
        origTran => origTran.legacyOrigTranId == rowData.legacyOrigTranId,
      )
    }
    const cp = this.counterPartyList.find(
      counterParty => counterParty.name == rowData.counterParty,
    )

    let createdDate = new Date().toISOString()
    if (
      isBulkEditModal(this.transInput) &&
      currentOriginalTransaction?.createdDate
    ) {
      createdDate = new Date(
        currentOriginalTransaction?.createdDate,
      ).toISOString()
    }
    let account = rowData.account
    if (rowData.sleeveAccountId) {
      account = rowData.sleeveAccountId?.toString()
    }

    const transactionData: BulkFXForwardTransaction = {
      counterParty: cp && cp.id != -1 ? cp : getDefaultUnknownCounterParty(),
      counterPartyId:
        cp && cp.id != -1 ? cp.id : getDefaultUnknownCounterParty().id,
      tranType: 'CURRENCY_FORWARD',
      userId: this.userId ?? 0,
      accountId: Number(account),
      entryDate: rowData.entryDate,
      settleDate: rowData.settleDate,
      postDate: rowData.settleDate,
      dataMap: currentOriginalTransaction?.dataMap
        ? currentOriginalTransaction.dataMap
        : {},
      traderId: 0,
      factor: 1.0,
      typeLabel: rowData.transactionTypeLabel,
    }

    if (rowData.cleared) {
      transactionData.clearedDate = isValidate
        ? new Date().toISOString().split('Z')[0]
        : new Date().toISOString()
    }

    let unclearTransaction = false
    if (currentOriginalTransaction?.clearedDate && !rowData.cleared) {
      unclearTransaction = true
    }

    if (!isValidate) {
      transactionData.sellToBuyRate =
        Number(rowData.fromUnits) / Number(rowData.toUnits)

      transactionData.revisionId = currentOriginalTransaction?.revisionId
        ? currentOriginalTransaction.revisionId + 1
        : 1
      transactionData.sourceId = TransactionConstants.HELIOS_SOURCE_ID
      transactionData.createdDate = createdDate
      transactionData.modifiedDate = new Date().toISOString()
      transactionData.active = true
      transactionData.commission = 0
    }

    if (rowData.toUnits) {
      transactionData.toUnits = Number(rowData.toUnits)
      transactionData.units = Number(rowData.toUnits)
    }

    if (rowData.fromUnits) {
      transactionData.fromUnits = Number(rowData.fromUnits)
    }

    const metaData: MetaDataTransaction = {
      rowId: Number(rowData.rowId),
      reconDate: this.sessionReconDate ?? this.selectedReconDate ?? new Date(),
      cusip: rowData.toSecurityCusip,
      fromSecurityCusip: rowData.fromSecurityCusip,
      toSecurityCusip: rowData.toSecurityCusip,
      unclearTransaction: unclearTransaction,
    }
    if (isBulkExternalModal(this.transInput)) {
      metaData.externalAccountId = rowData.externalAccountId
      metaData.executionId = rowData.executionId
      metaData.externalTranId = rowData.externalTranId
      metaData.externalCusip = rowData.externalCusip
      metaData.externalTranStatus = rowData.externalTranStatus
      metaData.dataSourceType = rowData.externalDataSourceType
    }

    if (currentOriginalTransaction) {
      transactionData.transactionId = currentOriginalTransaction.transactionId
      transactionData.legacyOrigTranId =
        currentOriginalTransaction.legacyOrigTranId
      transactionData.legacyTranId = currentOriginalTransaction.legacyTranId
    }
    if (!isValidate && currentOriginalTransaction?.typeLabelId) {
      transactionData.typeLabelId = currentOriginalTransaction.typeLabelId
    }
    return { transactionData, metaData }
  }

  /** @internal */
  async loadAssetIdsIdsUsingSecurityIds(
    securityInputInfoTradeEdits: SecurityIdRequest[],
  ) {
    const securityIdsPromise =
      this.bulkTransactionService.fetchSecurityDetailsFromSecurityIds(
        securityInputInfoTradeEdits,
      )
    return securityIdsPromise.toPromise()
  }

  clearAll() {
    this.gridApi?.api.applyTransaction({
      remove: this.gridApi?.api.getRenderedNodes().map(rowNode => rowNode.data),
    })

    this.createFreshRowData()
    this.gridApi?.api.applyTransaction({ add: this.rowData })
    this.cellLevelErrorMessageMap.clear()
    this.cellLevelWarningMessageMap.clear()
    this.errorDetails = ''
  }

  validateAndSave(isSaveValidRows?: boolean) {
    if (this.showConfirmButtonModal) {
      const modalRef = this.modalService.open(
        BulkTransactionsSaveConfirmationModalComponent,
        { keyboard: false },
      )
      modalRef.result.then((confirmed: boolean) => {
        if (confirmed) {
          return isSaveValidRows ? this.saveValidRows() : this.save()
        } else return
      })
    } else if (isSaveValidRows) this.saveValidRows()
    else this.save()
  }

  async saveValidRows() {
    await this.save(true)
    const newRowData: BulkFxForwardRow[] = []
    this.gridApi?.api.forEachNode(node => {
      if (
        node.data.validityStatus != 'valid' &&
        node.data.validityStatus != 'warning'
      )
        newRowData.push(node.data)
    })
    this.gridApi?.api.setRowData(newRowData)

    this.disableSaveValidRowsButton()
  }

  async save(onlyValidRows?: boolean) {
    if (!this.isDataValid && !onlyValidRows) {
      return
    }
    this.formSubmitting = true
    this.loadingText = TransactionConstants.SAVE_TRANSACTION
    await this.blankSessionService.createBlankSessionIfNotInSession()
    const tranCommand: BulkTransactionRequest = {
      transaction: onlyValidRows
        ? this.getGridOnlyValidTransactions(false)
        : this.getGridTransactions(false),
      tranCategory: TranCategory.CURRENCY_FORWARD,
      requestMode: bulkRequestMode(this.transInput?.modalMode),
    }
    if (this.note.value != '') {
      tranCommand.transactionNote = { newValue: this.note.value }
    }
    if (isBulkEditModal(this.transInput)) {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_FX_FORWARD_SAVE_NOTE_CW_EDIT
      this.bulkTransactionService
        .editBulkTransaction(tranCommand, TranCategoryUrl.CURRENCY_FORWARD)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk Transaction edit command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else if (isBulkExternalModal(this.transInput)) {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_FX_FORWARD_SAVE_NOTE_EXTERNAL
      this.bulkTransactionService
        .createBulkTransaction(tranCommand, TranCategoryUrl.CURRENCY_FORWARD)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk External Transaction command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    } else {
      tranCommand.notes =
        TransactionConstants.HELIOS_BULK_FX_Forward_SAVE_NOTE_CW_CREATE
      this.bulkTransactionService
        .createBulkTransaction(tranCommand, TranCategoryUrl.CURRENCY_FORWARD)
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(
          () => {
            this.toastService.success(
              'Bulk Transaction create command successfully submitted.',
            )
            this.formSubmitting = false
            if (!onlyValidRows) this.closeModal(true, tranCommand.transaction)
          },
          error => {
            this.errorHeader = TransactionConstants.ERROR_SAVING_TRANSACTION
            this.errorDetails = error.error
            this.formSubmitting = false
          },
        )
    }
  }
  generateEmptyDataEntry(activated: boolean, id: number): BulkFxForwardRow {
    return {
      rowId: id.toString(),
      account: '',
      entryDate: '',
      settleDate: '',
      fromSecurityCusip: '',
      toSecurityCusip: '',
      fromUnits: '',
      toUnits: '',
      cleared: false,
      counterParty: '',
      validityStatus: activated ? 'intermediate' : 'inactive',
      errorMessageMap: new Map(),
    }
  }

  generateDataEntryFromFx(
    cwTransaction: BulkFXForwardTransaction,
    index: number,
  ): BulkFxForwardRow {
    const fxForward = {
      legacyOrigTranId: cwTransaction.legacyOrigTranId,
      rowId: (index + 1).toString(),
      account: cwTransaction.accountId.toString(),
      entryDate: cwTransaction.entryDate,
      settleDate: cwTransaction.settleDate,
      fromSecurityCusip: cwTransaction.fromSecurityCusip ?? '',
      toSecurityCusip: cwTransaction.toSecurityCusip ?? '',
      fromUnits: cwTransaction.fromUnits?.toString() ?? '',
      toUnits: cwTransaction.toUnits?.toString() ?? '',
      counterParty: cwTransaction.counterParty?.name ?? 'Unknown',
      validityStatus: 'intermediate',
      errorMessageMap: new Map(),
      orderId: cwTransaction.orderId,
      cleared: cwTransaction.clearedDate ? true : false,
    } as BulkFxForwardRow
    if (cwTransaction.orderId) {
      fxForward.orderId = cwTransaction.orderId
    }
    return fxForward
  }

  generateDataEntryFromExternalFx(
    extTransaction: ExternalTransaction,
    index: number,
  ): BulkFxForwardRow {
    let accountId = extTransaction.accountId.toString()
    if ('CUSTODY' === extTransaction.dataSourceType) {
      if (this.hideAccountForSleeveAggregate(extTransaction.accountId)) {
        accountId = ''
      }
    }
    return {
      rowId: (index + 1).toString(),
      account: accountId.toString(),
      entryDate: extTransaction.tradeDate ?? '',
      settleDate: extTransaction.settleDate ?? '',
      fromSecurityCusip: extTransaction.fromAlternateId ?? '',
      toSecurityCusip: extTransaction.secIdentifier ?? '',
      fromUnits: extTransaction.fromUnits?.toString() ?? '',
      toUnits: extTransaction.units?.toString() ?? '',
      counterParty: extTransaction.clearwaterCounterParty?.name ?? 'Unknown',
      validityStatus: 'intermediate',
      errorMessageMap: new Map(),
      executionId: extTransaction.executionId,
      externalTranId: extTransaction.extTranId,
      externalAccountId: extTransaction.accountId,
      externalCusip: extTransaction.secIdentifier,
      externalTranStatus: extTransaction.status,
      externalDataSourceType: extTransaction.dataSourceType,
      transactionTypeLabel: extTransaction.transactionTypeLabel,
      cleared:
        this.isDateBeforeCurrentReconDate(extTransaction.settleDate) ?? false,
    }
  }

  isDateBeforeCurrentReconDate(date: string | undefined) {
    if (
      this.reconDate &&
      !isAfter(parseDate(date, ISODateFormat), this.reconDate)
    )
      return true
    return false
  }

  createFreshRowData() {
    this.rowData = []
    this.rowData.push(this.generateEmptyDataEntry(true, 1))
    for (let i = 1; i <= 9; i++) {
      this.rowData.push(this.generateEmptyDataEntry(false, i + 1))
    }
  }

  private closeModal(
    success: boolean,
    transactionRequests: TransactionRequestWrapper[],
  ): void {
    this.activeModal.close({
      success,
      transactionRequests,
    } as BulkTransactionModalResponse)
  }
  async loadExtTransactionDetailBulk(transInput: number[]) {
    const sessionAccountId = await this.store
      .select(selectFirstLoadedAccountId)
      .pipe(take(1))
      .toPromise()
    const transactionDetail$: Observable<ExternalUnmatchedTransaction[]> =
      this.bulkTransactionService.loadExtTransactionDetailBulk(
        transInput,
        sessionAccountId,
      )
    const accountInfo$ = this.store
      .select(selectFirstFocusedAccountInfo)
      .pipe(take(1))
    return Promise.all([
      transactionDetail$.toPromise(),
      accountInfo$.toPromise(),
    ]).then(async ([extTransactionDetail, accountInfo]) => {
      if (this.transInput) {
        // Create a Map with the transaction details keyed by their id for faster lookup
        const transactionDetailById = new Map(
          extTransactionDetail.map(td => [td.externalTransactionId, td]),
        )
        // Sort the transaction details based on the order of transInput
        this.bulkExternalTransactionDetail = transInput
          .map(id => transactionDetailById.get(id))
          .filter((td): td is ExternalUnmatchedTransaction => Boolean(td))
        await this.setSessionAccounInfo(accountInfo)
      }
    })
  }
  async loadTransactionDetailBulk(
    cwTransId: string[],
    securityId: number[] | undefined,
  ) {
    const transactionDetail$: Observable<ClearwaterTransactionDetailResponse> =
      this.bulkTransactionService.loadTransactionDetailBulk(
        cwTransId,
        securityId,
        TranCategoryUrl.CURRENCY_FORWARD,
      )
    return Promise.all([transactionDetail$.toPromise()]).then(
      ([transactionDetail]) => {
        if (this.transInput) {
          const unorderedTransactionDetails =
            transactionDetail.transactionDataMap
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const orderedTransactionDetails: { [key: string]: any } = {}

          // Sort the transaction details based on the order of cwTransid
          for (const id of cwTransId) {
            if (unorderedTransactionDetails[id]) {
              orderedTransactionDetails[id] = unorderedTransactionDetails[id]
            }
          }

          this.clearwaterTransactionDetail = orderedTransactionDetails
          this.securityMasterData = new Map<string, SecurityMasterData>(
            Object.entries(transactionDetail.securityMasterDataMap ?? ''),
          )
        }
      },
    )
  }

  disableGridFocus() {
    this.gridApi?.api.clearRangeSelection()
  }

  async loadNonSessionAccountTypeInfo(
    uniqueAccountId: number[],
  ): Promise<void> {
    const accountInfoPromise = this.bulkTransactionService
      .fetchAccountTypes(uniqueAccountId)
      .toPromise()
    await Promise.all([accountInfoPromise]).then(async accountTypeObj => {
      if (!accountTypeObj) return
      this.sleeveSimpleAccount = Object.entries(accountTypeObj[0])
        .map(([key, value]) => {
          return {
            id: Number(key),
            type: value,
          }
        })
        .filter(data => data.type === 'sleeveAggregate')

      if (this.sleeveSimpleAccount.length > 0) {
        this.sleeveAccountIds = this.sleeveSimpleAccount.map(({ id }) => id)

        const subAccounts = await this.bulkTransactionService
          .fetchAllSubAccounts(this.sleeveAccountIds)
          .toPromise()

        if (!subAccounts) return

        this.bulkSleeveSimpleAccount = Object.entries(subAccounts).map(
          ([key, value]) => {
            return {
              id: Number(key),
              sleeveSimple: value,
            }
          },
        )
      }
    })
  }

  isAccountEditable(params: EditableCallbackParams): boolean {
    if (
      'sleeveAggregate' === this.sessionAccountInfo?.accountType ||
      this.transInput?.externalTransaction
    ) {
      if ('sleeveAggregate' === this.sessionAccountInfo?.accountType) {
        return this.isSessionAccountEditable()
      } else {
        return this.isNonSessionAccountsEditable(params)
      }
    } else if (!this.isEdit) {
      return true
    }
    return false
  }

  isSessionAccountEditable() {
    return true
  }

  isNonSessionAccountsEditable(params: EditableCallbackParams) {
    if (this.sleeveAccountIds?.includes(params.data['externalAccountId'])) {
      return true
    }
    return false
  }

  async setSessionAccounInfo(accountInfo?: AccountInfo) {
    if (accountInfo) {
      this.sessionAccountInfo = accountInfo
      if ('sleeveAggregate' === accountInfo.accountType) {
        const subAccounts = await this.bulkTransactionService
          .fetchSubAccounts(accountInfo.accountId)
          .toPromise()
        this.sleeveSimpleAccount = subAccounts
      }
    }
  }

  hideAccountForSleeveAggregate(accountId: number) {
    if (
      'sleeveAggregate' === this.sessionAccountInfo?.accountType &&
      this.sessionAccountInfo?.accountId == accountId
    ) {
      return true
    } else if (this.sleeveAccountIds?.includes(accountId)) {
      return true
    }
    return false
  }

  @HostListener('click') dialogClicked() {
    if (isDialogComponentInActive(this.compId)) {
      activateDialog(this.activeInstances, this.compId, this.elRef)
    }
  }

  getComponentId() {
    return this.compId
  }

  copyHeaders() {
    const headers = this.gridApi?.columnApi
      .getAllDisplayedColumns()
      .filter(col => col.getColId() !== 'validityStatus')
      .map(col => col.getColDef().headerName)
    let headersTabSeparated = ''
    if (headers) headersTabSeparated = headers.join('\t')

    navigator.clipboard.writeText(headersTabSeparated)
    this.toastService.success('Copied to Clipboard')
  }

  clearUnclearTransaction(event: CellValueChangedEvent) {
    if (isBulkEditModal(this.transInput)) return

    const settleDate = event.node.data['settleDate']
    if (
      this.reconDate &&
      !isAfter(parseDate(settleDate, ISODateFormat), this.reconDate)
    ) {
      event.node.setDataValue('cleared', true)
    } else event.node.setDataValue('cleared', false)
  }
}
